import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Select, Table, message } from "antd";
import ConfidencePill from "../../../components/ConfidencePill";
import { normalizeConfidence } from "../utils";
import { fetchReceivedLogAPI } from "../../../service/mailIntake";
import {
  ListViewWrapper,
  ListToolbar,
  ListSearchInput,
  ListTableWrapper,
  StatusPill,
  MutedText,
  MetaText,
  ResultName,
  ReceivedScopeNote,
  ClientKindTag,
  TrailList,
  PreviewFrame,
  PrimaryBtn,
} from "../MailIntakeStyled";

const PAGE_SIZE = 25;

// "Everything" is the empty key: the API treats a missing filter as all.
const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "filed", label: "Filed" },
  { value: "waiting", label: "Waiting to be sorted" },
  { value: "skipped", label: "Skipped" },
  { value: "deleted", label: "Deleted" },
];

const CHANNEL_OPTIONS = [
  { value: "", label: "Arrived any way" },
  { value: "mail", label: "Mail (scanned)" },
  { value: "fax", label: "Fax" },
  { value: "va_download", label: "VA download" },
  { value: "upload", label: "Uploaded" },
];

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// One line under the status pill: what last happened, by whom, when.
// Skips the parts other columns already say — the status word itself,
// the filed-to client (Client column), and plain arrival (Received).
const describeStanding = (row) => {
  const event = row.last_event;
  if (!event || /^Received\b/.test(event.label || "")) return "";
  const label = row.status_label || "";
  let detail = event.label || "";
  if (label && detail.startsWith(label)) {
    detail = detail.slice(label.length).replace(/^\s*[—-]\s*/, "");
  }
  if (/^to\s/.test(detail)) detail = "";
  return [detail, event.by ? `by ${event.by}` : "", formatWhen(event.at)]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Received tab: every letter the mail room has seen since WHG Docs
 * started sorting (July 2026), any status — filed, waiting, skipped
 * or deleted — newest first. Built for the mail sorter's "this client
 * says he sent something, did we get it?" question (Matthew,
 * 2026-10-30): search by client (the filed matter or the computer's
 * guesses) or file name, see how and when it arrived and where it
 * stands, expand a row for the full trail, preview the pages.
 *
 * Read-only. Filing, skipping, deleting and putting back stay on the
 * Assign and Skipped tabs. Shown only to the people the server lists
 * (counts.viewer.sees_received_log).
 */
const ReceivedView = () => {
  const [data, setData] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusKey, setStatusKey] = useState("");
  const [channelKey, setChannelKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { url, title }

  // Latest request wins: a slow older response must never overwrite a
  // newer one, and typing invalidates whatever is in flight.
  const loadSeqRef = useRef(0);
  const debounceRef = useRef(null);
  const isFirstSearchEffect = useRef(true);

  const load = useCallback(
    async (targetPage, filters) => {
      const seq = ++loadSeqRef.current;
      setLoading(true);
      try {
        const response = await fetchReceivedLogAPI({
          page: targetPage,
          pageSize: PAGE_SIZE,
          search: filters.search,
          status: filters.status,
          channel: filters.channel,
        });
        if (seq !== loadSeqRef.current) return;
        setData(response?.data || { results: [], count: 0 });
        setPage(targetPage);
      } catch (error) {
        if (seq === loadSeqRef.current) {
          message.error(error?.message || "Could not load received mail.");
        }
      } finally {
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    []
  );

  // Mount + filter changes fetch immediately; only typing debounces.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    load(1, { search, status: statusKey, channel: channelKey });
    // `search` is deliberately not a dependency here — it has its own
    // debounced effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, statusKey, channelKey]);

  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return undefined;
    }
    loadSeqRef.current += 1;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => load(1, { search, status: statusKey, channel: channelKey }),
      350
    );
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, load]);

  const columns = [
    {
      title: "Received",
      dataIndex: "received_at",
      key: "received_at",
      width: 170,
      render: (value) => formatWhen(value),
    },
    {
      title: "Document",
      dataIndex: "document_name",
      key: "document_name",
      ellipsis: true,
      render: (name, row) => (
        <>
          <ResultName style={{ fontSize: 13 }}>{name || "—"}</ResultName>
          {row.page_count != null && (
            <MetaText>
              {row.page_count} {row.page_count === 1 ? "page" : "pages"}
            </MetaText>
          )}
        </>
      ),
    },
    {
      title: "How it arrived",
      dataIndex: "channel",
      key: "channel",
      width: 210,
      render: (channel) =>
        channel ? (
          <>
            <div>{channel.label}</div>
            {channel.detail && <MetaText>{channel.detail}</MetaText>}
          </>
        ) : (
          <MutedText>—</MutedText>
        ),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      width: 260,
      render: (client) => {
        if (!client?.name) return <MutedText>Not yet known</MutedText>;
        if (client.kind === "filed") {
          return <ResultName style={{ fontSize: 13 }}>{client.name}</ResultName>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <ResultName style={{ fontSize: 13 }}>{client.name}</ResultName>
            <ClientKindTag>guess</ClientKindTag>
            <ConfidencePill value={normalizeConfidence(client.confidence)} />
          </div>
        );
      },
    },
    {
      title: "Where it stands",
      dataIndex: "status",
      key: "status",
      width: 280,
      render: (statusValue, row) => {
        const standing = describeStanding(row);
        return (
          <>
            <StatusPill status={statusValue}>{row.status_label || statusValue}</StatusPill>
            {standing && (
              <MetaText style={{ display: "block", marginTop: 4 }}>{standing}</MetaText>
            )}
          </>
        );
      },
    },
    {
      title: "",
      key: "preview",
      width: 100,
      render: (_, row) =>
        row.preview_url ? (
          <PrimaryBtn
            style={{ padding: "5px 12px", fontSize: 12 }}
            onClick={(e) => {
              e.stopPropagation();
              setPreview({ url: row.preview_url, title: row.document_name || "Preview" });
            }}
          >
            Preview
          </PrimaryBtn>
        ) : (
          <MetaText>—</MetaText>
        ),
    },
  ];

  const currentFilters = { search, status: statusKey, channel: channelKey };

  return (
    <ListViewWrapper>
      <ListToolbar>
        <ListSearchInput
          placeholder="Search by client or file name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="received-search"
        />
        <Select
          value={statusKey}
          onChange={setStatusKey}
          options={STATUS_OPTIONS}
          style={{ minWidth: 190, fontFamily: "Manrope" }}
          popupMatchSelectWidth={false}
          data-testid="received-status"
        />
        <Select
          value={channelKey}
          onChange={setChannelKey}
          options={CHANNEL_OPTIONS}
          style={{ minWidth: 170, fontFamily: "Manrope" }}
          popupMatchSelectWidth={false}
          data-testid="received-channel"
        />
      </ListToolbar>
      <ReceivedScopeNote>
        Everything the mail room has received since WHG Docs started sorting in
        July 2026 — filed, waiting, skipped and deleted alike. Emails saved from
        Outlook do not pass through the mail room and are not listed here.
      </ReceivedScopeNote>

      <ListTableWrapper>
        <Table
          size="middle"
          rowKey="id"
          columns={columns}
          dataSource={data.results}
          loading={loading}
          locale={{ emptyText: "Nothing received matches this search" }}
          expandable={{
            expandRowByClick: true,
            rowExpandable: (row) => Array.isArray(row.trail) && row.trail.length > 0,
            expandedRowRender: (row) => (
              <TrailList data-testid={`received-trail-${row.id}`}>
                {row.trail.map((event, index) => (
                  <li key={`${row.id}-${index}`}>
                    <strong>{formatWhen(event.at)}</strong> — {event.label}
                    {event.by ? ` by ${event.by}` : ""}
                    {event.note ? <MetaText> · “{event.note}”</MetaText> : null}
                  </li>
                ))}
              </TrailList>
            ),
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data.count,
            showSizeChanger: false,
            onChange: (nextPage) => {
              clearTimeout(debounceRef.current);
              load(nextPage, currentFilters);
            },
          }}
        />
      </ListTableWrapper>

      <Modal
        open={Boolean(preview)}
        title={preview?.title}
        footer={null}
        width="80vw"
        onCancel={() => setPreview(null)}
        destroyOnClose
      >
        {preview && (
          <PreviewFrame
            src={preview.url}
            title={preview.title}
            data-testid="received-preview-frame"
          />
        )}
      </Modal>
    </ListViewWrapper>
  );
};

export default ReceivedView;
